import GridConnectionChargeMonitor from '@/components/grid-connection-charge-monitor';
import { useAppSelector } from '@/features';
import { getAllAssetsById } from '@/features/assets/selectors';
import { getChargerPowersSelector, getMeterPowersSelector } from '@/features/GridConnections';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/components/ui/accordion';
import type { Asset } from '@/types/swagger';
import { Link } from '@tanstack/react-router';
import { shallowEqual } from 'react-redux';

const GridConnectionsMonitor = () => {
  const assets = Array.from(useAppSelector(getAllAssetsById, shallowEqual)) as Asset[];
  const chargePointsByGridConnectionId = useAppSelector(
    (state) => state.chargePoints.byGridConnectionId,
    shallowEqual
  );
  const connectors = useAppSelector(
    (state) => state.connectors.onlineByGridConnectionId,
    shallowEqual
  );
  const metersPowers = useAppSelector(getMeterPowersSelector, shallowEqual);
  const chargersPowers = useAppSelector(getChargerPowersSelector, shallowEqual);

  return (
    <div className="asset-gc-list $tw-mt-6 $tw-space-y-6">
      {assets
        .filter((asset) => asset.grid_connections.length)
        .sort((a, b) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        })
        .map((asset) => (
          <Accordion key={asset.id} type="multiple" defaultValue={[`${asset.id}`]}>
            <AccordionItem
              value={`${asset.id}`}
              className="$tw-rounded-md $tw-border $tw-bg-foreground/5 $tw-p-4"
            >
              <AccordionTrigger className="$tw-p-0 hover:$tw-no-underline">
                <div className="$tw-flex $tw-w-full $tw-items-center $tw-justify-between $tw-gap-2">
                  <div className="$tw-flex $tw-flex-col $tw-items-start $tw-text-left">
                    <h2 className="$tw-m-0 $tw-text-base">{asset.name}</h2>
                    <p className="$tw-m-0 $tw-line-clamp-1 $tw-text-sm $tw-text-muted-foreground">{`${asset.address}, ${asset.town}, ${asset.region}`}</p>
                  </div>
                  <span className="$tw-hidden $tw-text-nowrap $tw-text-base $tw-text-muted-foreground md:$tw-flex">
                    {asset.grid_connections ? `${asset.grid_connections.length} ` : '0 '} Empalmes
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="$tw-flex $tw-flex-col $tw-gap-6 $tw-pt-6">
                {asset.grid_connections
                  .sort((a, b) => {
                    if (a.name > b.name) return 1;
                    if (a.name < b.name) return -1;
                    return 0;
                  })
                  .map((gridConnection) => {
                    const gridConnectionChargePoints = Object.values(
                      chargePointsByGridConnectionId?.[gridConnection.id] ?? []
                    );
                    const filteredConnectors =
                      connectors[gridConnection.id] && Object.keys(connectors[gridConnection.id])
                        ? Object.values(connectors[gridConnection.id]).filter(
                            (connector) => connector.connector_id !== 0
                          )
                        : [];

                    return (
                      <Link
                        key={gridConnection.id}
                        to="/charge-center/grid-connections/$gridConnectionId"
                        params={{ gridConnectionId: String(gridConnection.id) }}
                        className="hover:$tw-bg-foreground/10 hover:$tw-text-foreground hover:$tw-no-underline"
                      >
                        <GridConnectionChargeMonitor
                          gridConnection={gridConnection}
                          gridConnectionChargePoints={gridConnectionChargePoints}
                          metersPower={metersPowers[gridConnection.id]}
                          chargersPower={chargersPowers[gridConnection.id]}
                          connectors={filteredConnectors}
                        />
                      </Link>
                    );
                  })}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ))}
    </div>
  );
};

export default GridConnectionsMonitor;
